<template>
  <ModalComponent
    name="confirmEditSubmissionModal"
    width="600"
    height="auto"
    :adaptive="true"
    :click-to-close="true"
    @before-close="modalClosed"
  >
    <div
      class="flex flex-col justify-between bg-main-primary bg-opacity-[0.03] px-5 py-3"
    >
      <div class="flex justify-between py-3">
        <h2
          class="flex items-center gap-x-1.5 text-xl font-semibold text-gray-900 lg:text-lg"
        >
          <span class="text-md text-gray-400">
            {{ selectedFile.document_name_without_id }}</span>
          <button
            class="text-md text-gray-400"
            @click="downloadFile"
          >
            | Download
          </button>
        </h2>
        <h2
          v-if="docPages.length"
          class="text-md text-gray-400"
        >
          Page {{ currentDocIndex + 1 }} of {{ docPages.length }}
        </h2>
      </div>
      <div class="h-full w-full">
        <div class="mb-2 h-[500px]">
          <div
            v-if="documentLoading"
            class="flex h-[20v] w-full items-center justify-center bg-gray-100 py-16"
          >
            <PreloaderIcon class="mx-auto text-zuno-turquoise opacity-100" />
          </div>
          <div v-else>
            <div v-if="previewUrl">
              <div class="inline-block h-[500px] w-full overflow-auto">
                <img
                  id="lodedImageId"
                  :src="previewUrl"
                  class="block h-full w-full object-fill shadow-sm transition-all"
                  alt="Preview"
                >
              </div>
              <div
                class="relative w-full rounded-lg after:inset-x-0 after:top-1/3 after:block after:h-0.5 after:-translate-y-1/2 after:rounded-lg"
              >
                <ol
                  class="relative z-10 -mt-10 flex w-full items-center justify-between px-8 text-sm font-medium text-gray-900"
                >
                  <li class="p-2">
                    <div class="group w-full text-center">
                      <button
                        class="top-4 rounded-full border-2 border-slate-500 px-1 text-xs text-gray-900 outline-none"
                        @click="zoomout"
                      >
                        <span>&minus;</span>
                      </button>
                    </div>
                  </li>
                  <li class="mr-2 p-2">
                    <div class="group w-full text-center">
                      <button
                        class="-left-6 top-4 rounded-full border-2 border-slate-500 px-1 text-xs text-gray-900 outline-none"
                        @click="zoomin"
                      >
                        <span>&plus;</span>
                      </button>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="docPages.length && !documentLoading"
          class="flex h-full w-full gap-x-1 overflow-x-auto py-2"
        >
          <button
            v-for="imageIndex in docPages"
            :key="`image-${imageIndex}`"
            :disabled="imageIndex === currentDocIndex"
            class="relative my-1 inline-block shrink-0 rounded-[.95rem]"
            @click="fetchFilePages(imageIndex)"
          >
            <img
              class="inline-block h-[150px] w-[100px] shrink-0"
              :class="{ 'blur-sm': currentDocIndex !== imageIndex }"
              :src="previewUrl"
              alt="Zuno image"
            >
          </button>
        </div>
      </div>
      <div class="flex justify-end gap-2 border-t-2 pt-2">
        <GhostButton @click="$modal.hide('confirmEditSubmissionModal')">
          Cancel
        </GhostButton>
      </div>
    </div>
  </ModalComponent>
</template>

<script>
export default {
  name: 'EsgDataEntryDocumentPreview',

  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    fileLoading: {
      type: Boolean,
      required: true,
    },
    fileIndex: {
      type: Number,
      default: 0,
    },
    filePages: {
      type: Array,
      default: () => [],
    },
    attachedFile: {
      type: Object,
      required: true,
    },
    fileUrl: {
      type: String,
      default: '',
    },
  },

  emits: ['modal-closed', 'download', 'getPageIndex'],

  data() {
    return {
      documentLoading: this.fileLoading,
      docPages: this.filePages,
      currentDocIndex: this.fileIndex,
      previewUrl: this.fileUrl,
      previewZoomLevel: 1,
      selectedFile: this.attachedFile,
      numberOfTimesPreviewZoomed: 0,
    }
  },

  watch: {
    isOpen(val) {
      if (val) {
        this.$modal.show('confirmEditSubmissionModal')
      }
    },
    fileUrl(newUrl) {
      this.previewUrl = newUrl
    },
    attachedFile(newFile) {
      this.selectedFile = newFile
    },
    fileLoading(loadStatus) {
      this.documentLoading = loadStatus
    },
    fileIndex(index) {
      this.currentDocIndex = index
    },
    filePages(newPage) {
      this.docPages = newPage
    },
  },

  methods: {
    closeModal() {
      this.$modal.hide('confirmEditSubmissionModal')
    },
    modalClosed() {
      this.$emit('modal-closed')
    },
    zoomin() {
      this.numberOfTimesPreviewZoomed++
      const image = document.getElementById('lodedImageId')
      this.previewZoomLevel += 0.25
      image.style.transform = `scale(${this.previewZoomLevel})`
      image.style.transformOrigin = 'top left'
    },
    zoomout() {
      if (this.previewZoomLevel === 1) return
      this.numberOfTimesPreviewZoomed--
      const image = document.getElementById('lodedImageId')
      this.previewZoomLevel -= 0.25
      image.style.transform = `scale(${this.previewZoomLevel})`
      image.style.transformOrigin = 'top left'
    },
    downloadFile() {
      this.$emit('download', this.selectedFile)
    },
    fetchFilePages(index) {
      this.currentDocIndex = index
      this.$emit('getPageIndex', index)
    },
  },
}
</script>
